import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Rating } from 'react-simple-star-rating';
import { useAuth } from '../../contexts/AuthContext';
import { reviewService } from '../../services/reviewService';
import { Review } from '../../types/firestore';
import { Alert, Button, FormControl, FormLabel, Spinner } from '../common/UIComponents';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

interface ReviewFormData {
  comment: string;
}

interface ReviewFormProps {
  repairRequestId: string;
  serviceProviderId: string;
  onReviewAdded?: (review: Review) => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  repairRequestId,
  serviceProviderId,
  onReviewAdded,
}) => {
  const { currentUser } = useAuth();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReviewFormData>({
    defaultValues: {
      comment: '',
    },
  });

  const [currentRating, setCurrentRating] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizeRatingValue = (value: number): number => {
    const normalized = value > 5 ? value / 20 : value;
    const roundedToHalf = Math.round(normalized * 2) / 2;
    return Math.max(0, Math.min(5, roundedToHalf));
  };

  useEffect(() => {
    setCurrentRating(0);
    setError(null);
    reset({ comment: '' });
  }, [repairRequestId, reset]);

  const handleRatingChange = (value: number) => {
    setCurrentRating(normalizeRatingValue(value));
    setError(null);
  };

  const onSubmit = async (data: ReviewFormData) => {
    if (!currentUser) {
      setError('Zaloguj się, aby wystawić opinię.');
      return;
    }

    const normalizedComment = data.comment.trim();
    if (currentRating < 1 || currentRating > 5) {
      setError('Wybierz ocenę od 1 do 5 gwiazdek (co 0.5).');
      return;
    }
    if (normalizedComment.length < 3) {
      setError('Komentarz powinien mieć co najmniej 3 znaki.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const reviewId = await reviewService.addReview({
        requestId: repairRequestId,
        rating: currentRating,
        comment: normalizedComment,
      });

      const displayName = currentUser.displayName || currentUser.email?.split('@')[0] || 'Klient';

      onReviewAdded?.({
        id: reviewId,
        uid: reviewId,
        requestId: repairRequestId,
        repairRequestId,
        serviceProviderId,
        clientId: currentUser.uid,
        rating: currentRating,
        comment: normalizedComment,
        clientDisplayName: displayName,
        verified: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    } catch (submitError: any) {
      setError(submitError?.message || 'Nie udało się zapisać opinii.');
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return <Alert variant="info">Zaloguj się, aby wystawić opinię do zakończonego zlecenia.</Alert>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {error && (
        <Alert variant="error" icon={<ExclamationCircleIcon className="w-5 h-5" />}>
          {error}
        </Alert>
      )}

      <FormControl error={currentRating < 1 && error ? 'Ocena jest wymagana.' : undefined}>
        <FormLabel required>Ocena realizacji</FormLabel>
        <div className="mt-1">
          <Rating
            onClick={handleRatingChange}
            initialValue={currentRating}
            allowFraction={true}
            transition
            size={30}
            fillColor="currentColor"
            className="text-gold-300"
            emptyStyle={{ display: 'flex' }}
            fillStyle={{ display: '-webkit-inline-box' }}
            readonly={loading}
          />
          <p className="text-xs text-primary-light-400 mt-1">
            Ocena: {(currentRating || 0).toFixed(1)}/5
          </p>
        </div>
      </FormControl>

      <div>
        <FormLabel required>Krótki komentarz</FormLabel>
        <textarea
          id="review-comment"
          rows={4}
          className={`
            form-input mt-1 block w-full bg-primary-dark-200 border border-primary-dark-300 rounded-lg
            text-primary-light-100 placeholder-primary-light-400
            focus:outline-none focus:ring-2 focus:ring-ratuj-300 focus:border-transparent
            disabled:opacity-50 disabled:cursor-not-allowed
            ${errors.comment ? 'border-status-error-500 focus:ring-status-error-500' : ''}
          `}
          placeholder="Napisz, jak przebiegła realizacja zlecenia."
          disabled={loading}
          {...register('comment', {
            required: 'Komentarz jest wymagany.',
            minLength: {
              value: 3,
              message: 'Komentarz powinien mieć co najmniej 3 znaki.',
            },
            maxLength: {
              value: 1000,
              message: 'Komentarz jest zbyt długi.',
            },
          })}
        />
        {errors.comment && <p className="form-error">{errors.comment.message}</p>}
      </div>

      <div className="pt-2">
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          className="py-2 px-4 text-sm font-medium"
        >
          {loading ? (
            <>
              <Spinner size="sm" color="white" className="mr-2" />
              Wysyłanie...
            </>
          ) : (
            'Wyślij opinię'
          )}
        </Button>
      </div>
    </form>
  );
};

export default ReviewForm;
